/**
 * 별채대화의 줄들 (시안 03 README 5.4·5.7·5.8·5.10, X1·X2).
 *
 * - RoomMemberMessage: 아바타 32 + [이름 → (필요 시) ↳ 인용 → 말풍선]. 얼굴·이름을 누르면 미니 프로필(N1).
 *   같은 멤버 연속이면 아바타·이름 자리를 32px 비우고 위 간격 4
 * - 부르기: 칩이 있으면 말풍선 아래 왼쪽 40 들여 쓰기. 지목(칩 없음)은 "{호칭} 차례". 고민 제안 칩은 --color-accent
 * - RoomWorryCard: 내 쪽(오른쪽) 최대 84%. 카드 전체가 버튼(→ 고민 스레드)
 * - NewMessagesPill, RoomSkeleton
 */
import { useEffect, useState } from 'react';
import { ArrowDown, ChevronDown } from 'lucide-react';
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';
import { Avatar } from '../../components/Avatar.jsx';
import { PostTag } from '../../components/Chips.jsx';
import { formatClock, worryCardStatus } from '../board/boardFeed.js';

function nameOf(memberKey) {
    return getMember(memberKey)?.name ?? '';
}

// 「나머지 얘기도 듣기」 칩 글(서버 REST_CHIP_TEXT 와 같음, SPEC_CHANGE_v1.3 22.3)
export const REST_CHIP_TEXT = '나머지 얘기도 듣기';

function ChoiceChip({ children, onClick, accent = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`min-h-10 rounded-[20px] border bg-surface px-4 py-2 text-[15px] leading-[22px] ${
                accent ? 'border-accent text-accent-text' : 'border-chip-line text-text-primary'
            }`}
        >
            {children}
        </button>
    );
}

export function RoomMemberMessage({
    message,
    showHead,
    quote,
    displayName,
    activeProfile,
    onOpenProfile,
    onAnswerHook,
    onAskBirth = null,
    star = null,
}) {
    const reactions = message.reactions ?? [];
    const { memberKey, hook } = message;
    const name = nameOf(memberKey);
    const openHook = hook && !hook.answered;
    return (
        <div className={`flex items-start gap-2 motion-safe:animate-message-in ${showHead ? 'mt-3' : 'mt-1'}`}>
            {showHead ? (
                <button
                    type="button"
                    aria-label={`${name} 미니 프로필`}
                    onClick={() => onOpenProfile(memberKey)}
                    className="shrink-0 rounded-full"
                >
                    <Avatar memberKey={memberKey} size={32} ring={activeProfile === memberKey} />
                </button>
            ) : (
                <span aria-hidden="true" className="w-8 shrink-0" />
            )}
            <div className="flex max-w-[84%] min-w-0 flex-col gap-1.5">
                {showHead ? (
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-hidden="true"
                        onClick={() => onOpenProfile(memberKey)}
                        className="flex items-center gap-1.5 self-start text-label"
                    >
                        {name}
                        {star ? <AboutUserStar fading={star === 'fading'} /> : null}
                    </button>
                ) : null}
                {quote ? (
                    <button
                        type="button"
                        onClick={() => onOpenProfile(quote.memberKey)}
                        className="self-start text-left text-[13px] leading-[18px] text-text-secondary"
                    >
                        ↳ {nameOf(quote.memberKey)}: {quote.text}
                    </button>
                ) : null}
                <p className="m-0 max-w-bubble self-start rounded-bubble rounded-tl-bubble-tail border border-hairline bg-surface px-3.5 py-2.5 font-serif text-body-member break-words whitespace-pre-wrap">
                    <span className="sr-only">{withJosa(name, '이', '가')} 말함: </span>
                    {message.body}
                </p>
                {reactions.length ? <ReactionPill reactions={reactions} /> : null}
                {openHook && hook.kind === 'worry' ? (
                    <div className="flex flex-wrap gap-2 pt-0.5">
                        <ChoiceChip accent onClick={() => onAnswerHook(message, null)}>
                            {hook.choices[0] ?? '고민 카드로 올려 둘까요?'}
                        </ChoiceChip>
                    </div>
                ) : null}
                {hook?.kind === 'birth' && onAskBirth ? (
                    <div className="flex flex-wrap gap-2 pt-0.5">
                        <ChoiceChip accent onClick={onAskBirth}>
                            생일 알려주기
                        </ChoiceChip>
                    </div>
                ) : null}
                {openHook && hook.kind === 'more_voices' ? (
                    // 「다들」 턴: 3명 뒤 「나머지 얘기도 듣기」 + 남은 멤버 이름 줄(시안 10 R3, 결정 40)
                    <div className="flex flex-col items-start gap-1 pt-0.5">
                        <button
                            type="button"
                            onClick={() => onAnswerHook(message, REST_CHIP_TEXT)}
                            className="min-h-10 rounded-[20px] border border-chip-line bg-surface px-4 py-2 text-[15px] leading-[22px] font-semibold text-brand"
                        >
                            {REST_CHIP_TEXT}
                        </button>
                        {hook.choices.length ? (
                            <span className="text-[12px] leading-4 text-label-muted">
                                {hook.choices.map(nameOf).join(', ')} 얘기가 남았어요
                            </span>
                        ) : null}
                    </div>
                ) : null}
                {openHook && !['worry', 'more_voices'].includes(hook.kind) && hook.choices.length ? (
                    <div className="flex flex-col gap-1.5 pt-0.5">
                        <div className="flex flex-wrap gap-2">
                            {hook.choices.map((choice) => (
                                <ChoiceChip key={choice} onClick={() => onAnswerHook(message, choice)}>
                                    {choice}
                                </ChoiceChip>
                            ))}
                        </div>
                        {hook.kind === 'experience' ? (
                            <span className="text-[13px] text-text-secondary">칩 대신 직접 써도 돼요</span>
                        ) : null}
                    </div>
                ) : null}
                {openHook && !['worry', 'birth', 'more_voices'].includes(hook.kind) && !hook.choices.length ? (
                    <span className="text-[13px] text-text-secondary">{displayName} 차례</span>
                ) : null}
            </div>
        </div>
    );
}

export function RoomWorryCard({ message, post, onOpen }) {
    const status = worryCardStatus(post);
    const avatarKeys = post?.responderKeys ?? [];
    const answered = new Set(post?.answeredMemberKeys ?? []);
    return (
        <div className="mt-3 flex justify-end motion-safe:animate-message-in">
            <button
                type="button"
                onClick={onOpen}
                aria-label={`고민 스레드 열기: ${message.body.slice(0, 30)}`}
                className="flex w-[84%] flex-col gap-3 rounded-card border border-hairline bg-surface p-4 text-left"
            >
                <span className="flex w-full items-center justify-between">
                    <PostTag kind="worry" />
                    <span className="text-[13px] text-text-secondary">{formatClock(message.createdAt)}</span>
                </span>
                <span className="text-body break-words whitespace-pre-wrap">{message.body}</span>
                <span className="flex w-full items-center gap-2">
                    <span className="flex gap-1">
                        {avatarKeys.map((memberKey) => (
                            <span
                                key={memberKey}
                                className={status.dim || !answered.has(memberKey) ? 'opacity-45' : ''}
                            >
                                <Avatar memberKey={memberKey} size={24} />
                            </span>
                        ))}
                    </span>
                    <span className="min-w-0 flex-1 text-[13px] text-text-secondary">{status.text}</span>
                    {status.link ? (
                        <span className="shrink-0 text-[13px] font-semibold text-brand">{status.link}</span>
                    ) : null}
                </span>
            </button>
        </div>
    );
}

/** 「이모, 오름이 답했어요」 · 넷 이상이면 「이모 외 3명이 답했어요」 (숫자 배지 없음) */
export function newPillLabel(memberKeys) {
    const names = memberKeys.map((key) => getMember(key)?.name ?? '');
    if (names.length >= 4) {
        return `${names[0]} 외 ${names.length - 1}명이 답했어요`;
    }
    const last = names.at(-1) ?? '';
    return `${[...names.slice(0, -1), withJosa(last, '이', '가')].join(', ')} 답했어요`;
}

/**
 * 새 말 알약 (시안 04 README 5.10, D1c). 위로 올려 읽는 중에 새 멤버 말이 오면 입력창 위 16px 가운데.
 * 말한 멤버 얼굴 24(최대 3, 겹침 -8) + 「{이름}, {이름}이 답했어요」 + 아래 화살표. 숫자 배지 없음
 */
export function NewMessagesPill({ memberKeys, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="absolute bottom-4 left-1/2 flex h-10 max-w-[calc(100%-32px)] -translate-x-1/2 items-center gap-2 rounded-[20px] border border-float-line bg-surface pr-3.5 pl-1.5 motion-safe:animate-message-in"
        >
            <span className="flex shrink-0">
                {memberKeys.slice(0, 3).map((key, index) => (
                    <span key={key} className={`rounded-full border-2 border-surface ${index ? '-ml-2' : ''}`}>
                        <Avatar memberKey={key} size={24} />
                    </span>
                ))}
            </span>
            <span className="truncate text-[13px] font-semibold">{newPillLabel(memberKeys)}</span>
            <ArrowDown size={16} strokeWidth={1.6} aria-hidden="true" className="shrink-0 text-brand" />
        </button>
    );
}

/** 불러오는 중 (시안 04 D1d): 상단 바 아래 2px 줄, 트랙 brand-tint · 막대 brand 34% 가 좌우로 오감. 진행률 아님 */
export function LoadingBar() {
    return (
        <div
            role="progressbar"
            aria-label="새 말을 받는 중"
            className="relative h-0.5 shrink-0 overflow-hidden bg-brand-tint"
        >
            <span className="absolute inset-y-0 left-0 w-[34%] bg-brand motion-safe:animate-loading-sweep" />
        </div>
    );
}

/** 처음 불러오는 중 (X2): 카드 자리 180 + 말풍선 막대 4개. 반짝임 없음 */
export function RoomSkeleton() {
    return (
        <div aria-hidden="true" className="flex flex-col gap-3 pt-5">
            <span className="h-[180px] rounded-card bg-surface-sunken" />
            <span className="h-12 w-3/4 rounded-bubble rounded-tl-bubble-tail bg-surface-sunken" />
            <span className="h-12 w-2/3 self-end rounded-bubble rounded-tr-bubble-tail bg-surface-sunken" />
            <span className="h-12 w-4/5 rounded-bubble rounded-tl-bubble-tail bg-surface-sunken" />
            <span className="h-12 w-1/2 rounded-bubble rounded-tl-bubble-tail bg-surface-sunken" />
        </div>
    );
}

/** 기기가 아직 알림 권한을 정하지 않았는지(물을 수 있는지) */
// 웹은 Notification 권한, 앱(안드로이드)은 기기 권한(DEV-12 · lib/push.js)
export { canAskPush } from '../../lib/push.js';

/**
 * 다음 날 아침 카드 아래 이모 한 줄 + [알림 켜기][괜찮아요] (시안 05 결정 19, S0b-8). 한 번만.
 * @param {{ line: string, onAnswer: (allow: boolean) => void }} props
 */
export function PushAskLine({ line, onAnswer }) {
    return (
        <div className="mt-3 flex items-start gap-2 motion-safe:animate-message-in">
            <Avatar memberKey="imo" size={32} />
            <div className="flex min-w-0 flex-col gap-1.5">
                <span className="text-label">{getMember('imo').name}</span>
                <p className="m-0 max-w-bubble self-start rounded-bubble rounded-tl-bubble-tail border border-hairline bg-surface px-3.5 py-2.5 font-serif text-body-member break-words">
                    {line}
                </p>
                <div className="flex flex-wrap gap-2 pt-0.5">
                    <ChoiceChip accent onClick={() => onAnswer(true)}>
                        알림 켜기
                    </ChoiceChip>
                    <ChoiceChip onClick={() => onAnswer(false)}>괜찮아요</ChoiceChip>
                </div>
            </div>
        </div>
    );
}

// ── 돌아왔을 때 보이는 것 (SPEC_CHANGE_v1.3 15.5, DESIGN_SPEC 6장, C-20b · D67) ─────────────────

/** '내 얘기' 별 점: 이름 오른쪽 6, 점 6px --star. 한 번 보이면 사라짐(페이드 160ms) */
export function AboutUserStar({ fading = false }) {
    return (
        <span
            aria-label="내 얘기"
            className={`inline-block size-1.5 rounded-full bg-star transition-opacity duration-160 ${
                fading ? 'opacity-0' : 'opacity-100'
            }`}
        />
    );
}

/** 「여기까지 봤어요」 선: 날짜 구분선과 같은 틀, 위 16 · 아래 8 */
export function SeenLine({ lineRef }) {
    return (
        <div ref={lineRef} role="separator" aria-label="여기까지 봤어요" className="flex items-center gap-3 pt-4 pb-2">
            <span aria-hidden="true" className="h-px flex-1 bg-hairline" />
            <span aria-hidden="true" className="text-section text-label-muted">
                여기까지 봤어요
            </span>
            <span aria-hidden="true" className="h-px flex-1 bg-hairline" />
        </div>
    );
}

/** 1~3일 비운 단골의 요약 줄(2줄까지, '내 얘기' 먼저) */
export function ReturnSummary({ lines }) {
    return (
        <div className="mt-3 flex flex-col gap-1 px-2 text-center">
            {lines.map((line) => (
                <p
                    key={`${line.kind}-${line.memberKey}`}
                    className="m-0 text-[13px] leading-[18px] text-text-secondary"
                >
                    {line.kind === 'about_user' ? `${nameOf(line.memberKey)}: ${line.text}` : line.text}
                </p>
            ))}
        </div>
    );
}

/** 접힌 묶음(S1-i): "안 본 사이 별채 얘기 {n}개" + 꺾쇠, '내 얘기'가 있으면 둘째 줄 "{호칭} 얘기도 있어" */
export function ReturnFold({ count, hasAboutUser, displayName, onOpen }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            className={`mx-auto mt-3 flex flex-col items-center justify-center gap-0.5 rounded-[18px] bg-surface-sunken pr-3 pl-4 ${
                hasAboutUser ? 'h-[52px]' : 'h-9'
            }`}
        >
            <span className="flex items-center gap-1 text-micro text-text-primary">
                안 본 사이 별채 얘기 {count}개
                <ChevronDown size={14} strokeWidth={1.8} aria-hidden="true" />
            </span>
            {hasAboutUser ? (
                <span className="text-[12px] leading-4 font-semibold text-accent-text">{displayName} 얘기도 있어</span>
            ) : null}
        </button>
    );
}

/** 3일 넘게 비운 단골: 이모의 「그동안 별채 소식」 카드 하나(장면 요약을 이모 틀에, 생성 없음) */
export function ReturnNewsCard({ news }) {
    return (
        <div className="mt-3 flex items-start gap-2">
            <Avatar memberKey="imo" size={32} />
            <div className="flex max-w-[84%] min-w-0 flex-col gap-1.5">
                <span className="text-label">{nameOf('imo')}</span>
                <div className="rounded-card border border-hairline bg-surface px-3.5 py-3">
                    <p className="m-0 text-micro text-label-muted">{news.title}</p>
                    <ul className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0">
                        {news.lines.map((line) => (
                            <li key={`${line.memberKey}-${line.text}`} className="font-serif text-body-member">
                                {line.text}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

// ── 조용한 리액션 (SPEC_CHANGE_v1.3 22.4, handoff/10_handoff_reaction R0 · Q1~Q4, C-20c · D70) ──────────

const SHORT_NAMES = { imo: '이모', oreum: '오름', son: '손 팀장', eunbyeol: '은별', do: '도 대표' };

/** 리액션 줄 글: 「도 대표 끄덕」, 둘이면 「이모 박수 · 오름 휘파람」 */
export function reactionLabel(reactions) {
    return reactions.map((item) => `${SHORT_NAMES[item.memberKey] ?? nameOf(item.memberKey)} ${item.text}`).join(' · ');
}

/**
 * R1d 묶음 알약: 초상 18 겹침 + 글자. 단골 말 아래는 오른쪽 · 바탕 surface + hairline, 멤버 말 아래는 왼쪽 · sunken.
 * 말풍선 뒤 300~800ms 에 180ms 로 나타남(동작 줄이기면 바로). 버튼 아님
 */
export function ReactionPill({ reactions, onUser = false }) {
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setShown(true), 300 + Math.random() * 500);
        return () => {
            clearTimeout(timer);
        };
    }, []);
    if (!reactions?.length) {
        return null;
    }
    const label = reactionLabel(reactions);
    return (
        <div role="list" aria-label={label} className={`mt-1 flex ${onUser ? 'justify-end' : 'justify-start'}`}>
            <span
                role="listitem"
                className={`inline-flex h-[26px] items-center gap-1.5 rounded-[13px] pr-2.5 pl-1 transition-opacity duration-180 motion-reduce:opacity-100 motion-reduce:transition-none ${
                    shown ? 'opacity-100' : 'opacity-0'
                } ${onUser ? 'border border-hairline bg-surface' : 'bg-surface-sunken'}`}
            >
                <span aria-hidden="true" className="flex">
                    {reactions.map((item, index) => (
                        <span
                            key={`${item.memberKey}-${item.key}`}
                            className={`rounded-full border-[1.5px] ${onUser ? 'border-surface' : 'border-surface-sunken'} ${
                                index ? '-ml-[5px]' : ''
                            }`}
                        >
                            <Avatar memberKey={item.memberKey} size={18} />
                        </span>
                    ))}
                </span>
                <span className="text-[12px] leading-4 whitespace-nowrap text-text-secondary">{label}</span>
            </span>
        </div>
    );
}
